import { useQuery } from '@vue/apollo-composable'
import { computed } from '@vue/composition-api'
import GetStockItemsWithCustomerPriceQuery from '../graphql/GetStockItemsWithCustomerPriceQuery.graphql'

export const useGetStockItemsWithCustomerPrice = (companyId, customerId) => {
	const { result, loading, error, onResult, onError, refetch } = useQuery(
		GetStockItemsWithCustomerPriceQuery,
		() => ({
			company_id: companyId.value || companyId,
			customer_id: customerId.value || customerId,
		}),
		{
			fetchPolicy: 'network-only',
			enabled: () =>
				!!(companyId.value || companyId) && !!(customerId.value || customerId),
		},
	)

	// Process the stock items with customer-specific pricing
	const stockItemsWithPrices = computed(() => {
		if (!result.value) return []

		const stocks = result.value.stock || []
		const customers = result.value.customers || []

		// Get customer's price class (A, B, C, or D)
		const customerPriceClass = customers[0]?.customer_price_class || 'A'
		const priceField = `stock_price_${customerPriceClass.toLowerCase()}`

		// Map stocks with their customer-specific price
		return stocks.map((stock) => ({
			stock_id: stock.stock_id,
			stock_name: stock.stock_name,
			stock_price_a: stock.stock_price_a,
			stock_price_b: stock.stock_price_b,
			stock_price_c: stock.stock_price_c,
			stock_price_d: stock.stock_price_d,
			customerPrice: stock[priceField] || 0,
			customerPriceClass: customerPriceClass,
		}))
	})

	// Get just the customer price class
	const customerPriceClass = computed(() => {
		if (!result.value || !result.value.customers) return 'A'
		return result.value.customers[0]?.customer_price_class || 'A'
	})

	// Execute query manually
	const executeQuery = async (compId, custId) => {
		try {
			const result = await refetch({
				company_id: compId || companyId.value || companyId,
				customer_id: custId || customerId.value || customerId,
			})
			return result
		} catch (err) {
			console.error('Error fetching stock items with customer price:', err)
			throw err
		}
	}

	return {
		stockItemsWithPrices,
		customerPriceClass,
		loading,
		error,
		onResult,
		onError,
		executeQuery,
		refetch,
	}
}

// Alternative implementation as a standalone function for one-time fetches
export const getStockItemsWithCustomerPrice = async (
	apolloClient,
	companyId,
	customerId,
) => {
	try {
		const { data } = await apolloClient.query({
			query: GetStockItemsWithCustomerPriceQuery,
			variables: {
				company_id: companyId,
				customer_id: customerId,
			},
			fetchPolicy: 'network-only',
		})

		if (!data) return { stocks: [], customerPriceClass: 'A' }

		const stocks = data.stock || []
		const customers = data.customers || []
		const customerPriceClass = customers[0]?.customer_price_class || 'A'
		const priceField = `stock_price_${customerPriceClass.toLowerCase()}`

		// Map stocks with their customer-specific price
		const stocksWithPrices = stocks.map((stock) => ({
			stock_id: stock.stock_id,
			stock_name: stock.stock_name,
			stock_price_a: stock.stock_price_a,
			stock_price_b: stock.stock_price_b,
			stock_price_c: stock.stock_price_c,
			stock_price_d: stock.stock_price_d,
			customerPrice: stock[priceField] || 0,
			customerPriceClass: customerPriceClass,
		}))

		return {
			stocks: stocksWithPrices,
			customerPriceClass: customerPriceClass,
		}
	} catch (error) {
		console.error('Error fetching stock items with customer price:', error)
		throw error
	}
}
